import React, { useState } from 'react'
import styled from 'styled-components'
import toast from 'react-hot-toast'

import { Helmet } from 'react-helmet'
import { NavLink as RouterNavLink, NavLinkProps, useHistory, useParams } from 'react-router-dom'
import {
  Breadcrumbs as MuiBreadcrumbs,
  Button as MuiButton,
  Card as MuiCard,
  CardContent,
  Divider as MuiDivider,
  Grid,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core'
import { spacing } from '@material-ui/system'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  LocalShipping as LocalShippingIcon,
  LocationOn as LocationOnIcon,
  Print as PrintIcon,
  TrackChanges as TrackChangesIcon,
} from '@material-ui/icons'

import { HierarchyPath } from 'src/components/Tree'
import {
  BooleanField,
  useQueryPaginator,
  DateTimeField,
  DetailTable,
  JSONField,
  LinkedField,
  LinkedPathField,
  PaginatedTable,
  ProfileField,
  RelatedLinkedField,
  ValueField,
} from 'src/components/Table'
import { DamagedListTable } from 'src/components/Table/list/Damaged'
import { UnitListTable } from 'src/components/Table/list/Unit'

import { useAsyncPrint } from 'src/components/ToPrint/useAsyncPrint'
import { skuit } from 'src/services/skuit'
import { Container, ContainerASN } from 'src/api/models'
import { ContainerDialog } from 'src/modules/supply_chain/components/ContainerDialog'
import { ContainerLabel } from 'src/components/PrintLabel'
import { useMutation, useQuery, UseQueryResult } from 'react-query'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

const Button = styled(MuiButton)(spacing)

const Card = styled(MuiCard)(spacing)

const Divider = styled(MuiDivider)(spacing)

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing)

const Centered = styled.div`
  text-align: center;
`

interface DetailsProps {
  container: UseQueryResult<Container>
  refresh?: () => void
}

const Details = (props: DetailsProps) => {
  const { container, refresh } = props
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const { print, renderPrintComponent } = useAsyncPrint()
  const [containerEditDialogOpen, setContainerEditDialogOpen] = useState(false)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const history = useHistory()

  const { mutate: markLost } = useMutation(skuit.sc.containers.lost, {
    onSuccess: () => {
      toast.success(`Successfully sent container to lost`)
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.response.data}`)
    },
  })

  const { mutate: deleteContainer } = useMutation(skuit.sc.containers.delete, {
    onSuccess: () => {
      toast.success(`Successfully deleted ${container.data?.name}`)
      history.push('/warehouse/containers')
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.response.data}`)
    },
  })

  return (
    <Card mb={6}>
      {renderPrintComponent()}
      <ContainerDialog
        open={containerEditDialogOpen}
        handleClose={() => {
          setContainerEditDialogOpen(false)
        }}
        update
        container={container.data}
        onSave={refresh}
      />
      <CardContent>
        <Centered>
          <LocationOnIcon />
          <Typography variant="h6" gutterBottom>
            {container && container.data?.name}
          </Typography>
          <Grid container justify="center" spacing={4}>
            {container && (
              <Grid item>
                <IconButton size="small" onClick={() => setContainerEditDialogOpen(true)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Grid>
            )}
            <Grid item>
              <IconButton
                size="small"
                onClick={() =>
                  print((props) => <ContainerLabel {...props} obj={container.data as Container} />)
                }
              >
                <PrintIcon fontSize="small" />
              </IconButton>
            </Grid>
            <Grid item>
              <IconButton size="small" onClick={handleClick}>
                <TrackChangesIcon fontSize="small" />
              </IconButton>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                <MenuItem
                  onClick={() => {
                    handleClose()
                    container.data?.guid && markLost({ guid: container.data?.guid })
                  }}
                >
                  Mark Container lost
                </MenuItem>
              </Menu>
            </Grid>
            {(container.data?.canRetire || container.data?.canDelete) && (
              <Grid item>
                <IconButton
                  size="small"
                  onClick={() => container.data?.guid && deleteContainer(container.data?.guid)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Grid>
            )}
          </Grid>
          <DetailTable
            obj={container}
            fields={[
              { id: 'guid', label: 'SquID', renderer: ValueField },
              { id: 'name', label: 'Name', renderer: ValueField },
              {
                id: 'facility',
                label: 'Facility',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
              { id: 'place', label: 'Place', renderer: RelatedLinkedField(ValueField, 'name') },
              { id: 'containerType', label: 'Container Type', renderer: ValueField },
              { id: 'retiredOn', label: 'Retired On', renderer: DateTimeField },
              { id: 'retiredFrom.objPath', label: 'Retired From', renderer: LinkedPathField },
              { id: 'isLost', label: 'Is Lost', renderer: BooleanField },
              { id: 'forShipping', label: 'For Shipping', renderer: BooleanField },
              { id: 'internalNotes', label: 'Internal Notes', renderer: ValueField },
              { id: 'reviewNeeded', label: 'Review Needed', renderer: BooleanField },
              { id: 'reviewReason', label: 'Review Reason', renderer: ValueField },
            ]}
          />
        </Centered>
      </CardContent>
    </Card>
  )
}

interface ASNDetailsProps {
  asn: UseQueryResult<ContainerASN>
}

const ASNDetails = (props: ASNDetailsProps) => {
  const { asn } = props
  return (
    <Card mb={6}>
      <CardContent>
        <Centered>
          <LocalShippingIcon />
          <Typography variant="h6" gutterBottom>
            Advanced Shipping Notice
          </Typography>
          <DetailTable
            obj={asn}
            fields={[
              { id: 'guid', label: 'SquID', renderer: ValueField },
              { id: 'trackingNumber', label: 'Tracking Number', renderer: ValueField },
              { id: 'billOfLadingNumber', label: 'Bill of Lading Number', renderer: ValueField },
            ]}
            customFields={asn.data?.customFields}
          />
        </Centered>
      </CardContent>
    </Card>
  )
}

interface PathParams {
  guid: string
}

export const ContainerPage = () => {
  const { guid } = useParams<PathParams>()
  const container = useQuery<Container>(['container-details', guid], () =>
    skuit.sc.containers.retrieve(guid)
  )
  const [containerDialogOpen, setContainerDialogOpen] = useState(false)

  const unitsPaginator = useQueryPaginator('unit-list', skuit.sc.units.list, {
    params: { page_size: 5, containers: guid },
  })
  const damagedPaginator = useQueryPaginator('damaged-list', skuit.sc.damaged.list, {
    params: { page_size: 5, containers: guid },
  })
  const barcodedPaginator = useQueryPaginator('barcoded-unit-list', skuit.sc.barcodedUnits.list, {
    params: { page_size: 5, containers: guid },
  })
  const lostPaginator = useQueryPaginator('lost-list', skuit.sc.lost.list, {
    params: { page_size: 5, containers: guid },
  })
  const foundPaginator = useQueryPaginator('found-list', skuit.sc.found.list, {
    params: { page_size: 5, containers: guid },
  })
  const eventsPaginator = useQueryPaginator('event-list', skuit.sc.events.list, {
    params: { page_size: 5, references: guid },
  })

  return (
    <>
      <Helmet title="Container" />
      {containerDialogOpen && (
        <ContainerDialog
          open={containerDialogOpen}
          handleClose={() => {
            setContainerDialogOpen(false)
          }}
        />
      )}

      <Grid justify="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Container
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} exact to="/">
              Dashboard
            </Link>
            <Link component={NavLink} exact to="/warehouse/containers">
              Containers
            </Link>
            <Typography>{container.data?.name}</Typography>
          </Breadcrumbs>
        </Grid>
        <Grid item>
          <div>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setContainerDialogOpen(true)}
            >
              <AddIcon />
              New Container
            </Button>
          </div>
        </Grid>
      </Grid>

      <Divider my={6} />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Grid container spacing={6}>
            <Grid item xs={12} lg={6}>
              {container && <Details container={container} refresh={container.refetch} />}
            </Grid>
          </Grid>
        </Grid>
        {!!container.data?.asn && (
          <Grid item xs={12} lg={6}>
            <ASNDetails asn={{ ...container, data: container.data.asn }} />
          </Grid>
        )}
        <Grid item xs={12}>
          {container && <HierarchyPath title="Storage Path" location={container} />}
        </Grid>
        <Grid item xs={12}>
          <UnitListTable
            paginator={unitsPaginator}
            title="Units"
            fields={['productName', 'product', 'sku', 'client', 'numUnits', 'dateReceived']}
          />
        </Grid>
        <Grid item xs={12}>
          <DamagedListTable
            paginator={damagedPaginator}
            fields={['guid', 'client', 'product', 'sku']}
          />
        </Grid>
        <Grid item xs={12}>
          <PaginatedTable
            title="Barcoded Units"
            fields={[
              { id: 'guid', label: 'Name', renderer: LinkedField(ValueField) },
              {
                id: 'client.name',
                label: 'Client',
                ordering: 'account__name',
                renderer: ValueField,
              },
              {
                id: 'product.name',
                label: 'Product',
                ordering: 'product__name',
                renderer: ValueField,
              },
            ]}
            paginator={barcodedPaginator}
          />
        </Grid>
        <Grid item xs={12}>
          <PaginatedTable
            title="Lost Here"
            fields={[
              {
                id: 'sku',
                label: 'SKU',
                ordering: 'sku__sku',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
              {
                id: 'sku.client',
                label: 'Client',
                ordering: 'sku__account__name',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
              {
                id: 'product',
                label: 'Product',
                ordering: 'product__name',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
              {
                id: 'numUnits',
                label: 'Number of Units',
                ordering: 'num_units',
                renderer: ValueField,
              },
              {
                id: 'dateReceived',
                label: 'Date Units Received',
                ordering: 'date_received',
                renderer: DateTimeField,
              },
              {
                id: 'objPath',
                label: 'Last Seen',
                ordering: 'original_place__search_path',
                renderer: LinkedPathField,
              },
            ]}
            paginator={lostPaginator}
          />
        </Grid>
        <Grid item xs={12}>
          <PaginatedTable
            title="Found Here"
            fields={[
              {
                id: 'sku',
                label: 'SKU',
                ordering: 'sku__sku',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
              {
                id: 'sku.client',
                label: 'Client',
                ordering: 'sku__account__name',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
              {
                id: 'product',
                label: 'Product',
                ordering: 'product__name',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
              {
                id: 'numUnits',
                label: 'Number of Units',
                ordering: 'num_units',
                renderer: ValueField,
              },
              {
                id: 'objPath',
                label: 'Found',
                ordering: 'place__search_path',
                renderer: LinkedPathField,
              },
            ]}
            paginator={foundPaginator}
          />
        </Grid>
        <Grid item xs={12}>
          <PaginatedTable
            title="Container Events"
            fields={[
              { id: 'type', label: 'Type', renderer: ValueField },
              { id: 'data', label: 'Data', renderer: JSONField },
              { id: 'by', label: 'By', renderer: ProfileField },
              { id: 'on', label: 'Occurred On', renderer: DateTimeField },
            ]}
            paginator={eventsPaginator}
          />
        </Grid>
      </Grid>
    </>
  )
}
